import { useEffect, useLayoutEffect } from 'react';

let activeInertCount = 0;
let storedAriaHidden: string | null = null;
let storedInert = false;
let rootElement: HTMLElement | null = null;

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function getAppRoot(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.getElementById('__next');
}

export function _resetBackgroundInertState() {
  activeInertCount = 0;
  storedAriaHidden = null;
  storedInert = false;
  rootElement = null;
}

/**
 * When enabled, marks the Next.js app root as inert/aria-hidden so overlays
 * can trap interaction and screen-reader focus to their own content.
 */
export function useBackgroundInert(enabled: boolean): void {
  useIsomorphicLayoutEffect(() => {
    if (!enabled) return;

    const appRoot = getAppRoot();
    if (!appRoot) return;

    const previousCount = activeInertCount;
    activeInertCount++;

    if (previousCount === 0) {
      rootElement = appRoot;
      storedAriaHidden = appRoot.getAttribute('aria-hidden');
      storedInert = appRoot.hasAttribute('inert');
      appRoot.setAttribute('aria-hidden', 'true');
      appRoot.setAttribute('inert', '');
    }

    return () => {
      activeInertCount = Math.max(0, activeInertCount - 1);

      if (activeInertCount !== 0 || !rootElement) {
        return;
      }

      if (storedAriaHidden === null) {
        rootElement.removeAttribute('aria-hidden');
      } else {
        rootElement.setAttribute('aria-hidden', storedAriaHidden);
      }

      if (storedInert) {
        rootElement.setAttribute('inert', '');
      } else {
        rootElement.removeAttribute('inert');
      }

      storedAriaHidden = null;
      storedInert = false;
      rootElement = null;
    };
  }, [enabled]);
}
